<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="event-queue-dialog-title"
    @cancel.prevent="emit('close')"
    @click="onScrimClick"
  >
    <div class="panel">
      <div class="hero">
        <img
          class="hero-art"
          src="/assets/home/home-event.webp"
          alt=""
        >
        <div class="hero-shade"></div>
        <CloseXButton
          class="close"
          :label="t('changelog.dismiss')"
          @click="emit('close')"
        />
        <div class="hero-copy">
          <p class="kicker">{{ t('eventQueueDialog.kicker') }}</p>
          <h2 id="event-queue-dialog-title">{{ t('eventQueueDialog.title') }}</h2>
          <p v-if="until" class="until">{{ until }}</p>
        </div>
      </div>

      <div class="body">
        <p class="lead">
          <template v-for="(part, i) in leadParts" :key="i">
            <strong v-if="part.em">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <ul class="modes">
          <li
            v-for="id in EVENT_QUEUE_MODES"
            :key="id"
            class="mode"
            :class="{ live: id === liveMode }"
          >
            <p v-if="id === liveMode" class="live-tag">{{ t('eventQueueDialog.live') }}</p>
            <p class="name">{{ t(`eventQueue.modes.${id}`) }}</p>
            <p class="hint">{{ t(`eventQueueDialog.modes.${id}`) }}</p>
          </li>
        </ul>
        <p class="prizes">
          <template v-for="(part, i) in duelPayParts" :key="i">
            <strong v-if="part.em">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <p class="prizes">
          <template v-for="(part, i) in prizeParts" :key="i">
            <strong v-if="part.em">{{ part.text }}</strong>
            <template v-else>{{ part.text }}</template>
          </template>
        </p>
        <p class="board">
          {{
            t('home.eventRankHint', {
              rank: t('home.rank'),
              tab: t('rank.tabs.event'),
            })
          }}
        </p>
      </div>

      <div class="actions">
        <button type="button" class="ghost snooze" @click="emit('snooze')">
          {{ t('dialog.snoozeToday') }}
        </button>
        <button type="button" class="ghost" @click="emit('close')">
          {{ t('changelog.dismiss') }}
        </button>
        <router-link
          to="/rank?board=event"
          class="ghost"
          @click="emit('close')"
        >
          {{ t('eventQueueDialog.board') }}
        </router-link>
        <button type="button" class="cta" @click="emit('play')">
          {{ t('eventQueueDialog.play') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { EVENT_QUEUE_MODES } from '@shared/index'
import { useI18n } from '../i18n/t'
import { useSiteStore } from '../stores/site'
import CloseXButton from './CloseXButton.vue'

const emit = defineEmits<{ close: []; snooze: []; play: [] }>()
const { t } = useI18n()
const site = useSiteStore()
const dialogEl = ref<HTMLDialogElement | null>(null)

const liveMode = computed(() => site.eventQueue?.mode ?? null)

const until = computed(() => {
  const time = site.eventCountdown
  if (!time) return ''
  return t('eventQueue.endsIn', { time })
})

function boldParts(raw: string): { em: boolean; text: string }[] {
  const parts: { em: boolean; text: string }[] = []
  const pattern = /\*\*([^*]+)\*\*/g
  let last = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(raw))) {
    if (match.index > last) parts.push({ em: false, text: raw.slice(last, match.index) })
    parts.push({ em: true, text: match[1] ?? '' })
    last = match.index + match[0].length
  }
  if (last < raw.length) parts.push({ em: false, text: raw.slice(last) })
  if (parts.length === 0) parts.push({ em: false, text: raw })
  return parts
}

const leadParts = computed(() => boldParts(t('eventQueueDialog.lead')))
const duelPayParts = computed(() => boldParts(t('eventQueueDialog.duelPay')))
const prizeParts = computed(() => boldParts(t('eventQueueDialog.prizes')))

onMounted(() => {
  dialogEl.value?.showModal()
})

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) emit('close')
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  overflow: hidden;
  animation: event-queue-in var(--md-motion-medium) var(--md-motion-standard);
}

.scrim::backdrop {
  background: rgba(10, 6, 18, 0.74);
  backdrop-filter: blur(10px);
}

.panel {
  display: flex;
  flex-direction: column;
  width: min(720px, 100%);
  max-height: min(86vh, 760px);
  border-radius: 16px;
  overflow: hidden;
  background:
    radial-gradient(120% 80% at 0% 100%, rgba(168, 92, 255, 0.18), transparent 46%),
    radial-gradient(90% 70% at 100% 0%, rgba(226, 179, 64, 0.22), transparent 42%),
    #140b1c;
  border: 1px solid rgba(226, 179, 64, 0.55);
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(247, 232, 255, 0.1);
  color: #fff8ee;
}

.hero {
  position: relative;
  flex: 0 0 auto;
  min-height: 168px;
  overflow: hidden;
}

.hero-art {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 42%;
}

.hero-shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(20, 11, 28, 0.12) 0%, rgba(20, 11, 28, 0.86) 100%),
    linear-gradient(90deg, rgba(20, 11, 28, 0.55), transparent 55%);
}

.close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(20, 11, 28, 0.55);
  color: #fff8ee;
}

.hero-copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 168px;
  padding: 20px 24px 16px;
}

.kicker {
  margin: 0 0 4px;
  color: #e2b340;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

h2 {
  margin: 0;
  font-size: 32px;
  font-weight: 500;
  line-height: 1.15;
  text-shadow: 0 2px 16px rgba(0, 0, 0, 0.45);
}

.until {
  margin: 6px 0 0;
  color: #f3e4c4;
  font-size: 13px;
}

.body {
  overflow: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px 8px;
}

.lead,
.prizes,
.board {
  margin: 0;
  color: #f3e4c4;
  font-size: 14px;
  line-height: 20px;
}

.lead strong,
.prizes strong {
  color: #e2b340;
  font-weight: 700;
}

.modes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.mode {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 12px;
  border-radius: 12px;
  background: linear-gradient(180deg, rgba(247, 232, 255, 0.1), rgba(72, 28, 108, 0.38));
  border: 1px solid rgba(168, 92, 255, 0.28);
}

.mode.live {
  border-color: rgba(226, 179, 64, 0.7);
  box-shadow: inset 0 0 0 1px rgba(226, 179, 64, 0.28);
}

.live-tag {
  margin: 0;
  color: #e2b340;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.name {
  margin: 0;
  color: #fff8ee;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.hint {
  margin: 0;
  color: #f3e4c4;
  font-size: 13px;
  line-height: 18px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  padding: 8px 16px 16px;
}

.snooze {
  margin-right: auto;
}

.cta,
.ghost {
  min-height: 40px;
  padding: 8px 16px;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
}

.cta {
  display: inline-flex;
  align-items: center;
  border: 0;
  background: linear-gradient(180deg, #f0cb6a, #e2b340);
  color: #140b1c;
}

.ghost {
  display: inline-flex;
  align-items: center;
  border: 0;
  background: transparent;
  color: #f3e4c4;
}

.ghost:hover,
.cta:hover {
  filter: brightness(1.08);
}

@keyframes event-queue-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media (max-width: 640px) {
  h2 {
    font-size: 26px;
  }

  .modes {
    grid-template-columns: 1fr;
  }
}

@media (orientation: landscape) and (max-height: 520px) {
  .hero,
  .hero-copy {
    min-height: 0;
  }

  .hero-art,
  .hero-shade {
    display: none;
  }

  .hero-copy {
    padding: 10px 52px 8px 16px;
  }

  h2 {
    font-size: 22px;
  }

  .until {
    margin-top: 2px;
  }

  .modes {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .scrim {
    animation: none;
  }

  .scrim::backdrop {
    backdrop-filter: none;
  }
}
</style>
